import React, { useState, useEffect } from "react";
import QRCode from "qrcode";

const API = () => process.env.REACT_APP_DEFAULTPATH;

async function post(path, token, body) {
  const res = await fetch(`${API()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

const boxStyle = {
  border: "1px solid var(--border-color, rgba(128,128,128,0.35))",
  borderRadius: 8,
  padding: "10px 12px",
  background: "var(--bg-subtle, rgba(128,128,128,0.08))",
  wordBreak: "break-all",
  fontFamily: "monospace",
  fontSize: 14,
};

/** The ten one-time codes, shown once, with copy and download. */
export const BackupCodesView = ({ codes, onSaved, buttonLabel = "I have saved these codes" }) => {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  const download = () => {
    const blob = new Blob([`GuardLogix backup codes\nEach code works once.\n\n${text}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "guardlogix-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <p className="mb-2">
        <strong>Save these backup codes now.</strong> If you lose your phone, each code can be used once to sign in. They will not be shown again.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }} className="mb-3">
        {codes.map((c) => (
          <div key={c} style={boxStyle}>{c}</div>
        ))}
      </div>
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={download}>
          Download
        </button>
        <button type="button" className="btn btn-primary btn-sm ms-auto" onClick={onSaved}>
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

/**
 * Turn two-step verification on.
 *   token       a normal login token, or the setup token returned by login
 *   onComplete  called with the confirm reply ({ backupCodes, and token/userID/... when signing in) after the user confirms they saved the backup codes
 */
const MfaSetupPanel = ({ token, onComplete }) => {
  const [setup, setSetup] = useState(null); // { secret, otpauthUrl }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [qr, setQr] = useState("");
  const [keyCopied, setKeyCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { ok, data } = await post("mfa/setup/start", token);
      if (!alive) return;
      if (ok && data.secret) setSetup(data);
      else setError(data.error || "Could not start the setup. Please try again.");
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // The QR code is drawn in the browser from the setup link; the key never goes to another service
  useEffect(() => {
    if (!setup) return undefined;
    let alive = true;
    QRCode.toDataURL(setup.otpauthUrl, { width: 200, margin: 1 })
      .then((url) => alive && setQr(url))
      .catch(() => alive && setQr(""));
    return () => {
      alive = false;
    };
  }, [setup]);

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(setup.secret);
      setKeyCopied(true);
    } catch (e) {
      setKeyCopied(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { ok, data } = await post("mfa/setup/confirm", token, { code });
    setBusy(false);
    if (ok && data.backupCodes) setResult(data);
    else setError(data.error || "That code is not correct.");
  };

  if (result) {
    return <BackupCodesView codes={result.backupCodes} onSaved={() => onComplete(result)} buttonLabel="Done, continue" />;
  }

  if (!setup) {
    return error ? <div className="alert alert-danger py-2">{error}</div> : <p className="text-muted">Preparing…</p>;
  }

  return (
    <form onSubmit={confirm}>
      <ol className="ps-3 mb-3">
        <li className="mb-2">
          Install an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, Authy or similar).
        </li>
        <li className="mb-2">
          In the app choose <em>Add account</em> → <em>Scan a QR code</em> and scan this:
          <div style={{ marginTop: 8, display: "inline-block", background: "#ffffff", padding: 8, borderRadius: 8, lineHeight: 0 }}>
            {qr ? <img src={qr} alt="QR code for your authenticator app" width={200} height={200} /> : <div style={{ width: 200, height: 200 }} />}
          </div>
        </li>
        <li className="mb-2">
          Cannot scan? In the app choose <em>Enter a setup key</em>, account name: your email, type: <em>time-based</em>, and use this key (letters A–Z and digits 2–7, no spaces):
          <div style={{ ...boxStyle, marginTop: 6 }} data-testid="mfa-secret">{setup.secret}</div>
          <div className="mt-1 d-flex flex-wrap gap-3" style={{ fontSize: 12 }}>
            <button type="button" className="btn btn-link p-0" style={{ fontSize: 12 }} onClick={copyKey}>
              {keyCopied ? "Copied" : "Copy key"}
            </button>
            <span>
              On the same phone you can <a href={setup.otpauthUrl}>open it directly in the app</a>.
            </span>
          </div>
        </li>
        <li>Type the 6-digit code the app shows:</li>
      </ol>
      <input
        className="form-control mb-2"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        maxLength={7}
        value={code}
        onChange={(e) => {
          setCode(e.target.value.replace(/[^\d ]/g, ""));
          setError("");
        }}
        autoFocus
      />
      {error && <div className="alert alert-danger py-2">{error}</div>}
      <button type="submit" className="btn btn-primary w-100" disabled={busy || code.replace(/\s/g, "").length !== 6}>
        {busy ? "Checking…" : "Turn on two-step verification"}
      </button>
    </form>
  );
};

export default MfaSetupPanel;
